import React from 'react';
import classNames from 'classnames';
import Tooltip from 'rc-tooltip';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import IdeaCommunityRating from 'buyside/components/presentation/IdeaCommunityRating/ideaCommunityRating';
import IdeaReadingListButton from 'buyside/components/presentation/IdeaReadingListButton/ideaReadingListButton';
import Link from 'buyside/components/presentation/Link/link';
import {
  IdeaResearchItemProps,
  ResearchTypes,
  SharedResearchItemProps,
  WhitePaperResearchItemProps
} from 'buyside/components/presentation/ResearchItem/researchItem';

import BriefcaseIcon from 'buyside/svgs/briefcase.svg';

import {
  employerProfileUrl,
  ideaCommentsUrl,
  rankingUrl,
  userUrl,
  whitePaperCommentsUrl
} from 'buyside/util/urlHelper';

import styles from './researchItemFooterStyles.scss';

type SharedResearchItemFooterProps = Pick<SharedResearchItemProps, 'showUserDetails' | 'narrow'>;

type WhitePaperResearchItemFooterProps = Pick<
  WhitePaperResearchItemProps,
  'id' | 'commentsCount' | 'user'
> &
  SharedResearchItemFooterProps & {
    researchType: ResearchTypes.WHITE_PAPER;
  };

type IdeaResearchItemFooterProps = Pick<
  IdeaResearchItemProps,
  | 'id'
  | 'anonymous'
  | 'authorName'
  | 'commentsCount'
  | 'expectedPerformanceRating'
  | 'isNonBuyside'
  | 'user'
> &
  SharedResearchItemFooterProps & {
    researchType: ResearchTypes.IDEA;
    isPublic?: boolean;
  };

export const BriefcaseTooltip: React.FC = () => (
  <Tooltip overlay="This fund has a profile on SumZero's Cap Intro platform.">
    <BriefcaseIcon className={styles.briefcaseIcon} />
  </Tooltip>
);

const ContributorTypeTooltip: React.FC<{ isNonBuyside: boolean }> = ({ isNonBuyside }) => (
  <Tooltip overlay={isNonBuyside ? 'Non-Buyside' : 'Buyside'}>
    <span
      className={classNames(styles.userBadge, {
        [styles.blue]: !isNonBuyside
      })}
    >
      {isNonBuyside ? 'NB' : 'B'}
    </span>
  </Tooltip>
);

const ResearchItemFooter: React.FC<
  IdeaResearchItemFooterProps | WhitePaperResearchItemFooterProps
> = props => {
  const { id, commentsCount, user, showUserDetails, researchType, narrow } = props;

  const isIdea = researchType === ResearchTypes.IDEA;

  const mostRecentEmployer = user?.mostRecentEmployer;
  const isEmployerPresent =
    mostRecentEmployer?.employer?.fundType !== 'Other' &&
    mostRecentEmployer?.employer?.fundType !== 'Investment Bank';

  const shouldShowUserDetails = isIdea ? !props.anonymous && showUserDetails : showUserDetails;

  return (
    <section className={classNames(styles.researchItemFooter, { [styles.narrow]: narrow })}>
      <div className={styles.researchItemAuthor}>
        {shouldShowUserDetails ? (
          <div>
            <Link to={userUrl(user?.id)}>
              {isIdea ? props.authorName || user?.displayName : user?.displayName}
              <ContributorTypeTooltip isNonBuyside={isIdea ? Boolean(props.isNonBuyside) : false} />
            </Link>
            {mostRecentEmployer?.employer ? (
              <Link to={employerProfileUrl(mostRecentEmployer.employer.id)}>
                {mostRecentEmployer.employer.name}
                {mostRecentEmployer.employer.hasFundProfile ? <BriefcaseTooltip /> : null}
              </Link>
            ) : null}
          </div>
        ) : (
          <div>
            <span>
              <AnonymousUserDisplay
                anonUser={{
                  username: isIdea ? props.authorName : null,
                  title: mostRecentEmployer?.title
                }}
              />
              <ContributorTypeTooltip isNonBuyside={isIdea ? Boolean(props.isNonBuyside) : false} />
            </span>
            {isEmployerPresent ? (
              <AnonEmployerAssociation
                aumRange={mostRecentEmployer?.employer?.aumRange}
                employerStyles={styles.researchAnonEmployer}
                fundType={mostRecentEmployer?.employer?.fundType}
              />
            ) : null}
          </div>
        )}
        {user?.featuredRank && (isIdea ? !props.anonymous : true) ? (
          <div>
            <span className={styles.rankLabel}>
              {user?.featuredRank.current ? 'Current Ranking: ' : 'Recent Ranking: '}
            </span>
            <Link to={rankingUrl(user.featuredRank.category)}>{user.featuredRank.rankString}</Link>
          </div>
        ) : null}
      </div>
      {narrow ? null : (
        <div className={styles.researchItemActions}>
          {isIdea ? (
            <div className={styles.ideaCommunityRating}>
              <IdeaCommunityRating rating={props.expectedPerformanceRating} />
            </div>
          ) : null}
          <Button
            faIcon={faComment}
            text={`${commentsCount || 0} Comment${commentsCount === 1 ? '' : 's'}`}
            type={BUTTON_TYPES.LINK}
            url={isIdea ? ideaCommentsUrl(id) : whitePaperCommentsUrl(id)}
          />
          {isIdea && !props.isPublic ? <IdeaReadingListButton ideaId={id} /> : null}
        </div>
      )}
    </section>
  );
};

export default ResearchItemFooter;
